import {
  Bell,
  ChevronRight,
  CircleHelp,
  Eye,
  Gamepad2,
  Languages,
  Moon,
  Music2,
  RotateCcw,
  ShieldCheck,
  Smartphone,
  Sparkles,
  Sun,
  Vibrate,
  Volume2,
} from "lucide-react";
import type { LucideIcon } from "lucide-react";
import { SubpageHeader } from "../SubpageHeader/SubpageHeader";
import styles from "./SettingsView.module.css";

export type ThemePreference = "system" | "light" | "dark";

export interface PartySettings {
  sound: boolean;
  vibration: boolean;
  celebrations: boolean;
  reminders: boolean;
  theme: ThemePreference;
}

interface SettingsViewProps {
  settings: PartySettings;
  onBack: () => void;
  onMenuOpen: (trigger: HTMLButtonElement) => void;
  onReset: () => void;
  onSettingChange: <Key extends keyof PartySettings>(
    key: Key,
    value: PartySettings[Key],
  ) => void;
  onShowMessage: (message: string) => void;
}

interface SwitchRowProps {
  checked: boolean;
  description: string;
  icon: LucideIcon;
  label: string;
  tone: "violet" | "coral" | "mint" | "amber";
  onChange: (checked: boolean) => void;
}

const themeOptions = [
  { id: "system", label: "System", icon: Smartphone },
  { id: "light", label: "Ljust", icon: Sun },
  { id: "dark", label: "Mörkt", icon: Moon },
] as const;

function SwitchRow({
  checked,
  description,
  icon: Icon,
  label,
  tone,
  onChange,
}: SwitchRowProps) {
  return (
    <div className={styles.settingRow}>
      <span className={styles.settingIcon} data-tone={tone}>
        <Icon aria-hidden="true" />
      </span>
      <span className={styles.settingCopy}>
        <strong>{label}</strong>
        <small>{description}</small>
      </span>
      <button
        className={styles.switch}
        type="button"
        role="switch"
        aria-label={label}
        aria-checked={checked}
        onClick={() => onChange(!checked)}
      >
        <span />
      </button>
    </div>
  );
}

export function SettingsView({
  settings,
  onBack,
  onMenuOpen,
  onReset,
  onSettingChange,
  onShowMessage,
}: SettingsViewProps) {
  return (
    <>
      <SubpageHeader
        eyebrow="Gör det till ert"
        title="Inställningar"
        onBack={onBack}
        onMenuOpen={onMenuOpen}
      />

      <main className={styles.view}>
        <section className={styles.hero} aria-labelledby="settings-heading">
          <div className={styles.heroRing} aria-hidden="true" />
          <span className={styles.heroIcon}>
            <Gamepad2 aria-hidden="true" />
          </span>
          <div>
            <span className={styles.eyebrow}>
              <Sparkles aria-hidden="true" />
              Party på ert sätt
            </span>
            <h1 id="settings-heading">Ställ in stämningen</h1>
            <p>
              Finjustera ljud, känsla och utseende för hela spelkvällen.
            </p>
          </div>
        </section>

        <section className={styles.section} aria-labelledby="experience-heading">
          <div className={styles.sectionHeading}>
            <span className={styles.headingIcon}>
              <Volume2 aria-hidden="true" />
            </span>
            <div>
              <span className={styles.eyebrow}>Spelupplevelse</span>
              <h2 id="experience-heading">Känsla & tempo</h2>
            </div>
          </div>

          <div className={styles.settingList}>
            <SwitchRow
              checked={settings.sound}
              description="Musik, ljudeffekter och nedräkning"
              icon={Music2}
              label="Spelljud"
              tone="violet"
              onChange={(value) => onSettingChange("sound", value)}
            />
            <SwitchRow
              checked={settings.vibration}
              description="Små signaler vid svar och nya rundor"
              icon={Vibrate}
              label="Haptisk feedback"
              tone="mint"
              onChange={(value) => onSettingChange("vibration", value)}
            />
            <SwitchRow
              checked={settings.celebrations}
              description="Konfetti och effekter vid milstolpar"
              icon={Sparkles}
              label="Firanden"
              tone="coral"
              onChange={(value) => onSettingChange("celebrations", value)}
            />
            <SwitchRow
              checked={settings.reminders}
              description="Påminn när crewet brukar spela"
              icon={Bell}
              label="Spelpåminnelser"
              tone="amber"
              onChange={(value) => onSettingChange("reminders", value)}
            />
          </div>
        </section>

        <section className={styles.section} aria-labelledby="appearance-heading">
          <div className={styles.sectionHeading}>
            <span className={styles.headingIcon}>
              <Eye aria-hidden="true" />
            </span>
            <div>
              <span className={styles.eyebrow}>Utseende</span>
              <h2 id="appearance-heading">Välj atmosfär</h2>
            </div>
          </div>

          <div className={styles.themePicker} aria-label="Färgtema">
            {themeOptions.map(({ id, label, icon: Icon }) => (
              <button
                type="button"
                key={id}
                aria-pressed={settings.theme === id}
                onClick={() => onSettingChange("theme", id)}
              >
                <span>
                  <Icon aria-hidden="true" />
                </span>
                {label}
              </button>
            ))}
          </div>
        </section>

        <section className={styles.section} aria-labelledby="general-heading">
          <div className={styles.sectionHeading}>
            <span className={styles.headingIcon}>
              <Smartphone aria-hidden="true" />
            </span>
            <div>
              <span className={styles.eyebrow}>PartyCast</span>
              <h2 id="general-heading">Allmänt</h2>
            </div>
          </div>

          <div className={styles.linkList}>
            <button
              type="button"
              onClick={() =>
                onShowMessage("Svenska är redan valt som appspråk.")
              }
            >
              <Languages aria-hidden="true" />
              <span>
                <strong>Språk</strong>
                <small>Svenska</small>
              </span>
              <ChevronRight aria-hidden="true" />
            </button>
            <button
              type="button"
              onClick={() =>
                onShowMessage(
                  "Integritetscentret öppnas när kontotjänsten är ansluten.",
                )
              }
            >
              <ShieldCheck aria-hidden="true" />
              <span>
                <strong>Integritet</strong>
                <small>Data, samtycke och spelresultat</small>
              </span>
              <ChevronRight aria-hidden="true" />
            </button>
            <button
              type="button"
              onClick={() =>
                onShowMessage(
                  "Hjälpcentret öppnas när supportsidan är ansluten.",
                )
              }
            >
              <CircleHelp aria-hidden="true" />
              <span>
                <strong>Hjälp & feedback</strong>
                <small>Frågor, idéer och support</small>
              </span>
              <ChevronRight aria-hidden="true" />
            </button>
          </div>
        </section>

        <button className={styles.resetButton} type="button" onClick={onReset}>
          <RotateCcw aria-hidden="true" />
          Återställ standardinställningar
        </button>

        <p className={styles.version}>PartyCast Beta · Version 0.1.0</p>
      </main>
    </>
  );
}
